import * as moment from 'moment';
import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ReportWrapper } from './styles';
import { ColDef, ColGroupDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderGroupParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { IComparisonConversion, IComparisonConversionTotals } from '@nkr/models/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { OverlayTrigger } from 'react-bootstrap';
import PopoverTemplate from '@nkr/components/popover-template';
import { IComparisonConversionWithSelect } from './comparison-conversion-with-select';

type MyColumn = ColGroupDef | ColDef;

const getDonorSearchOrganParam = (organType: number) => {
    if (organType == 1) {
        return '&organ=0';
    }

    if (organType == 2) {
        return '&organ=1';
    }

    return '';
};

interface IProps {
    data: IComparisonConversion[],
    previousYear: number,
    currentYear: number,
    totals: IComparisonConversionTotals[],
    isAdmin: boolean,
    isLiverCenterView: boolean,
    centerId: number,
    organType: number,
    isMicrosite: number,
    includeDash: boolean,
    includeNkr: boolean,
    isAnnualized: boolean,
    onGridReady: (e: GridReadyEvent) => void
}

const Grid = (props: IProps) => {
    const getDrillThroughDateRange = (year: number) => {
        const isCurrentCalendarYear = year === moment().year();

        return {
            startDate: `01/01/${year}`,
            endDate: isCurrentCalendarYear ? moment().format('MM/DD/YYYY') : `12/31/${year}`
        };
    };

    const onGridReady = (e: GridReadyEvent) => {
        props.onGridReady(e);
        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const cols: MyColumn[] = [
        {
            headerName: '',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'rank',
                    headerName: '#',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>#</SortableHeaderComponent>,
                    headerClass: 'list-header-no-padding',
                    cellClass: 'list-cell-no-padding',
                    width: 35,
                    sortable: false,
                    valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
                },
                {
                    colId: 'centerAlias',
                    field: 'centerAlias',
                    headerName: 'Center',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
                    width: 125,
                    sortingOrder: ['asc', 'desc', null],
                    comparator: (valueA: string, valueB: string) => valueA.toLowerCase().localeCompare(valueB.toLowerCase()),
                },
            ]
        },
        {
            headerName: 'Center Details',
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'isDcnCenter',
                    field: 'isDcnCenter',
                    headerName: 'Donor Care Network',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>Donor<br />Care<br />Network</SortableHeaderComponent>,
                    width: 65,
                    hide: props.isLiverCenterView,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.donorCareNetworkCount}</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                return params.value ? <span className='label label-success'>Yes</span> : '--'
                            }
                        };
                    }
                },
                {
                    colId: 'isVoucherParticipant',
                    field: 'isVoucherParticipant',
                    headerName: 'Voucher Program',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>Voucher<br />Program</SortableHeaderComponent>,
                    width: 65,
                    hide: props.isLiverCenterView,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.voucherProgramCount}</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                return params.value ? <span className='label label-success'>Yes</span> : '--'
                            }
                        };
                    },
                },
                {
                    colId: 'isDsdCenter',
                    field: 'isDsdCenter',
                    headerName: 'DSD Kidney',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>Donor<br />Shield<br />Direct<br />Kidney</SortableHeaderComponent>,
                    width: 65,
                    hide: props.isLiverCenterView,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.donorShieldDirectCountKidney}</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                return params.value ? <span className='label label-success'>Yes</span> : '--'
                            }
                        };
                    }
                },
                {
                    colId: 'isDsdLiverCenter',
                    field: 'isDsdLiverCenter',
                    headerName: 'DSD Liver',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>Donor<br />Shield<br />Direct<br />Liver</SortableHeaderComponent>,
                    width: 65,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.donorShieldDirectCountLiver}</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                return params.value ? <span className='label label-success'>Yes</span> : '--'
                            }
                        };
                    }
                },
                {
                    colId: 'hasFollowUp',
                    field: 'hasFollowUp',
                    headerName: 'Follow-Up Enabled',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>Follow-Up<br />Enabled</SortableHeaderComponent>,
                    width: 75,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.followUpEnabledCount}</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                return params.value ? <span className='label label-success'>Yes</span> : '--'
                            }
                        };
                    }
                },
                {
                    colId: 'isPlDashCenter',
                    field: 'isPlDashCenter',
                    headerName: 'PL DASH Center',
                    headerClass: 'list-header',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>PL<br />DASH<br />Center</SortableHeaderComponent>,
                    width: 60,
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.plDashCenterCount}</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                return params.value ? <span className='label label-success'>Yes</span> : '--'
                            }
                        };
                    }
                },
                {
                    colId: 'isMicrositeCenter',
                    field: 'isMicrositeCenter',
                    headerName: 'Microsite Center',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>Microsite<br />Center</SortableHeaderComponent>,
                    width: 75,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.micrositeCenterCount}</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                return params.value ? <span className='label label-success'>Yes</span> : '--'
                            }
                        };
                    }
                },
                {
                    colId: 'isMicrositeCenterOfExcellence',
                    field: 'isMicrositeCenterOfExcellence',
                    headerName: 'Microsite Center Of Excellence',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>Microsite<br />Center<br />Of<br />Excellence</SortableHeaderComponent>,
                    width: 80,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.micrositeCenterOfExcellenceCount}</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                return params.value ? <span className='label label-success'>Yes</span> : '--'
                            }
                        };
                    }
                },
                {
                    colId: 'isInReferralNetwork',
                    field: 'isInReferralNetwork',
                    headerName: props.isLiverCenterView ? 'NLR Referral Network' : 'NKR Referral Network',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>{props.isLiverCenterView ? 'NLR' : 'NKR'}<br />Referral<br />Network</SortableHeaderComponent>,
                    width: 75,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.nkrReferralNetworkCount}</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                let data = params.data as IComparisonConversionWithSelect;

                                let refYes = props.isAdmin && data.isInReferralNetwork && !data.isVoucherParticipant
                                    ? <span className='label label-warning' title='Not in Voucher Program'>Yes</span>
                                    : <span className='label label-success'>Yes</span>
                                let refNo = props.isAdmin && !data.isInReferralNetwork && data.isMicrositeCenter
                                    ? <span className='label label-danger' title='Is Microsite Center but not selectable'>No</span>
                                    : '--'

                                return data.isInReferralNetwork ? refYes : refNo;
                            }
                        };
                    }
                },
                {
                    colId: 'isDonorConnectEnabled',
                    field: 'isDonorConnectEnabled',
                    headerName: 'PL Donor Mentor Enabled',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>PL Donor<br />Connect<br />Enabled</SortableHeaderComponent>,
                    width: 75,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.donorConnectEnabledCount}</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                return params.value ? <span className='label label-success'>Yes</span> : '--'
                            }
                        };
                    }
                },
                {
                    colId: 'isExpertSystem',
                    field: 'isExpertSystem',
                    headerName: 'Is KFL Expert System',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>KFL<br />Expert < br />System</SortableHeaderComponent>,
                    width: 75,
                    hide: props.isLiverCenterView,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.expertSystemCount}</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                return params.value ? <span className='label label-success'>Yes</span> : '--'
                            }
                        };
                    }
                },
            ]
        },
        {
            headerName: props.previousYear.toString(),
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'priorYearRegistrations',
                    field: 'priorYearRegistrations',
                    headerName: 'Registered',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>Registered</SortableHeaderComponent>,
                    width: 95,
                    hide: props.includeNkr,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.priorYearRegistrationsCount}</>;
                                },
                            };
                        }
                    }
                },
                {
                    colId: 'priorYearReferrals',
                    field: 'priorYearReferrals',
                    headerName: 'Referred',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>Referred</SortableHeaderComponent>,
                    width: 80,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.priorYearReferralsCount}</>;
                                },
                            };
                        }
                    }
                }
            ]
        },
        {
            headerName: props.currentYear.toString(),
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'reportYearRegistrations',
                    field: 'reportYearRegistrations',
                    headerName: 'Registered',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>Registered</SortableHeaderComponent>,
                    width: 95,
                    hide: props.includeNkr,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.reportYearRegistrationsCount}</>;
                                },
                            };
                        }
                    }
                },
                {
                    colId: 'reportYearReferrals',
                    field: 'reportYearReferrals',
                    headerName: 'Referred',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>Referred</SortableHeaderComponent>,
                    width: 80,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.reportYearReferralsCount}</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                let data = params.data as IComparisonConversion,
                                    { startDate: drillThroughStartDate, endDate: drillThroughEndDate } = getDrillThroughDateRange(props.currentYear),
                                    showLink = props.isAdmin || data.centerId == props.centerId,
                                    isDashEnabled = props.includeDash && props.isMicrosite !== 1,
                                    isNkrEnabled = props.includeNkr && props.isMicrosite !== 1,
                                    isMisiteEnabled = props.isMicrosite === 1 ? true : props.isMicrosite === 0 ? false : props.includeNkr,
                                    paramDash = isDashEnabled ? '1' : '0',
                                    paramsNkr = isNkrEnabled ? '1' : '0',
                                    paramsMisite = isMisiteEnabled ? '1' : '0',
                                    startDate = encodeURI(drillThroughStartDate),
                                    endDate = encodeURI(drillThroughEndDate),
                                    organType = getDonorSearchOrganParam(props.organType),
                                    siteUrl = `dash=${paramDash}&nkr=${paramsNkr}&ms=${paramsMisite}&sd=${startDate}&ed=${endDate}`,
                                    referralLink = `center/${data.centerId}/donors/donor-intake/search?${siteUrl}&d=-1&s=1&adv=1&stat=-1&rs=1&xtd=1${organType}`
                                    ;

                                if (showLink) {
                                    return (<a href={referralLink} style={{ textDecoration: 'underline' }} target='_blank'>{data.reportYearReferrals}</a>);
                                } else {
                                    return (<>{data.reportYearReferrals}</>);
                                }
                            }
                        };
                    }
                },
                {
                    colId: 'reportYearDonations',
                    field: 'reportYearDonations',
                    headerName: 'Donated',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>Donated</SortableHeaderComponent>,
                    width: 80,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.reportYearDonationsCount}</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                let data = params.data as IComparisonConversion,
                                    { startDate: drillThroughStartDate, endDate: drillThroughEndDate } = getDrillThroughDateRange(props.currentYear),
                                    showLink = props.isAdmin || data.centerId == props.centerId,
                                    isDashEnabled = props.includeDash && props.isMicrosite !== 1,
                                    isNkrEnabled = props.includeNkr && props.isMicrosite !== 1,
                                    isMisiteEnabled = props.isMicrosite === 1 ? true : props.isMicrosite === 0 ? false : props.includeNkr,
                                    paramDash = isDashEnabled ? '1' : '0',
                                    paramsNkr = isNkrEnabled ? '1' : '0',
                                    paramsMisite = isMisiteEnabled ? '1' : '0',
                                    startDate = encodeURI(drillThroughStartDate),
                                    endDate = encodeURI(drillThroughEndDate),
                                    organType = getDonorSearchOrganParam(props.organType),
                                    siteUrl = `dash=${paramDash}&nkr=${paramsNkr}&ms=${paramsMisite}&sd=${startDate}&ed=${endDate}`,
                                    donationLink = `center/${data.centerId}/donors/donor-intake/search?${siteUrl}&d=-1&s=2&adv=1&stat=-1&xtd=1${organType}`
                                    ;

                                if (showLink) {
                                    return (<a href={donationLink} style={{ textDecoration: 'underline' }} target='_blank'>{data.reportYearDonations}</a>);
                                } else {
                                    return (<>{data.reportYearDonations}</>);
                                }
                            }
                        };
                    }
                },
                {
                    colId: 'donatedCountAnnualized',
                    field: 'donatedCountAnnualized',
                    headerName: 'Donated (annualized)',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}><span style={{ color: 'red' }}>annualized</span><br />Donated</SortableHeaderComponent>,
                    width: 95,
                    hide: props.isAnnualized == false,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.donatedCountAnnualizedCount}</>;
                                },
                            };
                        }
                    }
                },
            ]
        },
        {
            headerName: 'Referral Conversion',
            headerGroupComponentFramework: (params: IHeaderGroupParams) =>
                <OverlayTrigger trigger='click' placement='left' rootClose overlay={
                    <PopoverTemplate title='Referral Conversion'>
                        <div className='text-center'>
                            Donations
                            <br />
                            -------------------
                            <br />
                            <b>AVG</b>(PriorYearReferrals, CurrentYearReferrals)
                        </div>
                    </PopoverTemplate>}>
                    <a style={{ color: 'yellow' }}>Referral Conversion</a>
                </OverlayTrigger>,
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'referredCountAverage',
                    field: 'referredCountAverage',
                    headerName: props.isAnnualized ? 'Avg Referred (annualized)' : 'Avg Referred',
                    headerComponentFramework: (params: IHeaderParams) =>
                        <SortableHeaderComponent {...params} sortAtTop={true}>
                            {props.isAnnualized ?
                                <><span style={{ color: 'red' }}>annualized</span><br />Avg<br />Referred</>
                                :
                                <>Avg<br />Referred</>
                            }
                        </SortableHeaderComponent>,
                    width: 80,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.referredCountAverageCount}</>;
                                },
                            };
                        }
                    }
                },
                {
                    colId: 'referralConversionRatePercent',
                    field: 'referralConversionRatePercent',
                    headerName: props.isAnnualized ? 'Conversion Rate (annualized)' : 'Conversion Rate',
                    headerComponentFramework: (params: IHeaderParams) =>
                        <SortableHeaderComponent {...params} sortAtTop={true}>
                            {props.isAnnualized ?
                                <><span style={{ color: 'red' }}>annualized</span><br />Conversion<br />Rate</>
                                :
                                <>Conversion<br />Rate</>
                            }
                        </SortableHeaderComponent>,

                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.referralConversionRatePercent}%</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                let data = params.data as IComparisonConversion;
                                return <>{data.referralConversionRatePercent}%</>
                            }
                        };
                    },
                    cellClassRules: {
                        'alternate-stripe': function (params) {
                            // Apply the 'alternate-stripe' class to alternate rows
                            if (!params.node.rowPinned)
                                return params.rowIndex % 2 === 1
                        },
                        'even-stripe': function (params) {
                            // Apply the 'even-stripe' class to even rows
                            if (!params.node.rowPinned)
                                return params.rowIndex % 2 === 0;
                        }
                    },
                    width: 85
                }
            ]
        },
        {
            groupId: 'registrationConversion',
            headerName: 'Registration Conversion',
            headerGroupComponentFramework: (params: IHeaderGroupParams) =>
                <OverlayTrigger trigger='click' placement='left' rootClose overlay={
                    <PopoverTemplate title='Registration Conversion'>
                        <div className='text-center' style={{ marginBottom: 20 }}>
                            Donations
                            <br />
                            -------------------
                            <br />
                            <b>AVG</b>(PriorYearRegistrations, CurrentYearRegistrations)
                        </div>
                        <p style={{ color: 'red', fontSize: 12 }} className='text-center'>* not available when viewing NKR referrals.</p>
                    </PopoverTemplate>}>
                    <a style={{ color: 'yellow' }}>Registration Conversion</a>
                </OverlayTrigger>,
            headerClass: 'list-group-header',
            hide: props.includeNkr,
            children: [
                {
                    colId: 'registeredCountAverage',
                    field: 'registeredCountAverage',
                    headerName: props.isAnnualized ? 'Avg Registered (annualized)' : 'Avg Registered',
                    headerComponentFramework: (params: IHeaderParams) =>
                        <SortableHeaderComponent {...params} sortAtTop={true}>
                            {props.isAnnualized ?
                                <><span style={{ color: 'red' }}>annualized</span><br />Avg<br />Registered</>
                                :
                                <>Avg<br />Registered</>
                            }
                        </SortableHeaderComponent>,
                    width: 90,
                    hide: props.includeNkr,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.registeredCountAverageCount}</>;
                                },
                            };
                        }
                    },


                },
                {
                    colId: 'registrationConversionRatePercent',
                    field: 'registrationConversionRatePercent',
                    headerName: props.isAnnualized ? 'Conversion Rate (annualized)' : 'Conversion Rate',
                    headerComponentFramework: (params: IHeaderParams) =>
                        <SortableHeaderComponent {...params} sortAtTop={true}>
                            {props.isAnnualized ?
                                <><span style={{ color: 'red' }}>annualized</span><br />Conversion<br />Rate</>
                                :
                                <>Conversion<br />Rate</>
                            }
                        </SortableHeaderComponent>,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.registeredConversionRatePercent}%</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                let data = params.data as IComparisonConversion;
                                return <>{data.registrationConversionRatePercent}%</>
                            }
                        };
                    },
                    cellClassRules: {
                        'alternate-stripe': function (params) {
                            // Apply the 'alternate-stripe' class to alternate rows
                            if (!params.node.rowPinned)
                                return params.rowIndex % 2 === 1
                        },
                        'even-stripe': function (params) {
                            // Apply the 'even-stripe' class to even rows
                            if (!params.node.rowPinned)
                                return params.rowIndex % 2 === 0;
                        }
                    },
                    width: 95,
                    hide: props.includeNkr,
                }
            ]
        },

        {
            headerName: props.isLiverCenterView ? 'NLR' : 'NKR',
            headerGroupComponentFramework: (params: IHeaderGroupParams) =>
                <OverlayTrigger trigger='click' placement='left' rootClose overlay={
                    <PopoverTemplate title={props.isLiverCenterView ? 'NLR Donations' : 'NKR Donations'}>
                        All Donations where the donor received Donor Shield Protections through the {props.isLiverCenterView ? 'NLR' : 'NKR'}
                    </PopoverTemplate>}>
                    <a style={{ color: 'yellow' }}>{props.isLiverCenterView ? 'NLR' : 'NKR'}</a>
                </OverlayTrigger>,
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'donatedNkrCount',
                    field: 'donatedNkrCount',
                    headerName: props.currentYear + ' Donated',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>{props.currentYear}<br />Donated</SortableHeaderComponent>,
                    width: 80,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.nkrDonationsCount}</>;
                                },
                            };
                        }
                    }
                },
                {
                    colId: 'nkrDonationPercent',
                    field: 'nkrDonationPercent',
                    headerName: props.isLiverCenterView ? '% NLR Donated' : '% NKR Donated',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>%<br />{props.isLiverCenterView ? 'NLR' : 'NKR'}<br />Donated</SortableHeaderComponent>,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.nkrConversionRatePercent}%</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                let data = params.data as IComparisonConversion;
                                return <>{data.nkrDonationPercent}%</>
                            }
                        };
                    },
                    cellClassRules: {
                        'alternate-stripe': function (params) {
                            // Apply the 'alternate-stripe' class to alternate rows
                            if (!params.node.rowPinned)
                                return params.rowIndex % 2 === 1
                        },
                        'even-stripe': function (params) {
                            // Apply the 'even-stripe' class to even rows
                            if (!params.node.rowPinned)
                                return params.rowIndex % 2 === 0;
                        }
                    },
                    width: 80,
                }
            ]
        },

        {
            headerName: 'Direct',
            headerGroupComponentFramework: (params: IHeaderGroupParams) =>
                <OverlayTrigger trigger='click' placement='left' rootClose overlay={
                    <PopoverTemplate title='Direct Donations'>
                        All direct donations, including NKR Internal Directs
                    </PopoverTemplate>}>
                    <a style={{ color: 'yellow' }}>Direct</a>
                </OverlayTrigger>,
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'donatedDirectCount',
                    field: 'donatedDirectCount',
                    headerName: props.currentYear + ' Donated',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>{props.currentYear}<br />Donated</SortableHeaderComponent>,
                    width: 80,
                    hide: props.isLiverCenterView,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.directDonatedCount}</>;
                                },
                            };
                        }
                    }
                },
                {
                    colId: 'directDonationPercent',
                    field: 'directDonationPercent',
                    headerName: '% Direct Donated',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>%<br />Direct<br />Donated</SortableHeaderComponent>,
                    hide: props.isLiverCenterView,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.directDonatedConversionRatePercent}%</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                let data = params.data as IComparisonConversion;
                                return <>{data.directDonationPercent}%</>
                            }
                        };
                    },
                    cellClassRules: {
                        'alternate-stripe': function (params) {
                            // Apply the 'alternate-stripe' class to alternate rows
                            if (!params.node.rowPinned)
                                return params.rowIndex % 2 === 1
                        },
                        'even-stripe': function (params) {
                            // Apply the 'even-stripe' class to even rows
                            if (!params.node.rowPinned)
                                return params.rowIndex % 2 === 0;
                        }
                    },
                    width: 80,
                }
            ]
        },

        {
            headerName: 'Disconnects',
            headerGroupComponentFramework: (params: IHeaderGroupParams) =>
                <OverlayTrigger trigger='click' placement='left' rootClose overlay={
                    <PopoverTemplate title='Disconnects'>
                        Disconnects are donors who donated directly after expressing interest in the voucher program and are not known to be a low eplet mismatch with the patient
                    </PopoverTemplate>}>
                    <a style={{ color: 'yellow' }}>Disconnects</a>
                </OverlayTrigger>,
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'donatedDisconnectCount',
                    field: 'donatedDisconnectCount',
                    headerName: props.currentYear + ' Disconnects',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>{props.currentYear}<br />Disconnects</SortableHeaderComponent>,
                    width: 90,
                    hide: props.isLiverCenterView,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.disconnectCount}</>;
                                },
                            };
                        }
                    }
                },
                {
                    colId: 'disconnectPercent',
                    field: 'disconnectPercent',
                    headerName: '% Voucher Disconnect',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>%<br />Voucher<br />Disconnect</SortableHeaderComponent>,
                    hide: props.isLiverCenterView,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.disconnectConversionRatePercent}%</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                let data = params.data as IComparisonConversion;
                                return <>{data.disconnectPercent}%</>
                            }
                        };
                    },
                    cellClassRules: {
                        'alternate-stripe': function (params) {
                            // Apply the 'alternate-stripe' class to alternate rows
                            if (!params.node.rowPinned)
                                return params.rowIndex % 2 === 1
                        },
                        'even-stripe': function (params) {
                            // Apply the 'even-stripe' class to even rows
                            if (!params.node.rowPinned)
                                return params.rowIndex % 2 === 0;
                        }
                    },
                    width: 90,
                }
            ]
        },

        {
            headerName: 'Contact Time',
            headerGroupComponentFramework: (params: IHeaderGroupParams) =>
                <OverlayTrigger trigger='click' placement='left' rootClose overlay={
                    <PopoverTemplate title='Disconnects'>
                        How many days between when the registration is created and first contact is made...
                    </PopoverTemplate>}>
                    <a style={{ color: 'yellow' }}>Contact Time</a>
                </OverlayTrigger>,
            headerClass: 'list-group-header',
            children: [
                {
                    colId: 'contactTimeDaysMedianPriorYear',
                    field: 'contactTimeDaysMedianPriorYear',
                    headerName: props.previousYear.toString() + ' Median',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>{props.previousYear}<br />Median</SortableHeaderComponent>,
                    width: 75,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.priorYearMedian}</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                let data = params.data as IComparisonConversion;
                                return <>{data.contactTimeDaysMedianPriorYear < 0 ? '--' : data.contactTimeDaysMedianPriorYear}</>
                            }
                        };
                    }
                },
                {
                    colId: 'contactTimeDaysMedian',
                    field: 'contactTimeDaysMedian',
                    headerName: props.currentYear.toString() + ' Median',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} sortAtTop={true}>{props.currentYear} < br /> Median</SortableHeaderComponent>,
                    width: 75,
                    cellRendererSelector: (selectorParams: any) => {
                        if (selectorParams.node.rowPinned) {
                            return {
                                frameworkComponent: (params: ICellRendererParams) => {
                                    let data = selectorParams.data as IComparisonConversionTotals;
                                    return <>{data.reportYearMedian}</>;
                                },
                            };
                        }

                        return {
                            frameworkComponent: (params: ICellRendererParams) => {
                                let data = params.data as IComparisonConversion;
                                return <>{data.contactTimeDaysMedian < 0 ? '--' : data.contactTimeDaysMedian}</>
                            }
                        };
                    }
                }
            ]
        },
    ];

    const columnDefs = useMemo(() => cols, [props]);
    const rowData = useMemo(() => props.data, [props.data]);
    const totalsData = useMemo(() => props.totals, [props.totals]);

    return (
        <ReportWrapper className='ag-theme-bootstrap' height={props.isAdmin ? '60vh' : null}>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true,
                    // sortingOrder: ['desc', null],
                    resizable: false,
                    cellClassRules: {
                        'totalsBackground': (params) => {
                            return params.data.centerAlias == 'TOTAL';
                        }
                    }
                }}
                columnDefs={cols}
                rowData={rowData}
                headerHeight={65}
                groupHeaderHeight={30}
                rowHeight={30}
                suppressCellSelection={true}
                suppressContextMenu={true}
                excelStyles={[
                    {
                        id: 'header',
                        alignment: {
                            horizontal: 'Center',
                            wrapText: true,
                        },
                        font: {
                            bold: true
                        }
                    },
                    {
                        id: 'cell',
                        alignment: {
                            horizontal: 'Center'
                        }
                    },
                    {
                        id: 'totalsBackground',
                        interior: {
                            color: '#ADD8E6',
                            pattern: 'Solid',
                            patternColor: 'black'
                        }
                    }
                ]}
                noRowsOverlayComponentFramework={() => <div>No centers found...</div>}
                getRowNodeId={(data: IComparisonConversionWithSelect) => data.centerId.toString()}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onSortChanged={onSortChanged}
                onRowDataChanged={onRowDataChanged}
                onGridSizeChanged={onGridSizeChanged}
                pinnedBottomRowData={[totalsData]}
                getRowStyle={(params: any) => {
                    var retColor = {};
                    if (params.node.rowPinned) {
                        return { backgroundColor: '#83afdd' } as any;
                    }
                    return retColor;
                }}
            />
        </ReportWrapper>
    );
}

export default Grid;
